import {
  Link,
  useLocation,
} from "react-router-dom";

import {
  motion,
} from "framer-motion";

import {
  useTranslation,
} from "react-i18next";

import {
  getLanguageFromPath,
  getRoutePath,
} from "../site/config/routes";

import AlbumShowcase from "../template/components/portfolio/AlbumShowcase";
import Footer from "../template/components/layout/Footer";
import HandwrittenEyebrow from "../template/components/common/HandwrittenEyebrow";

/*
  ============================================================
  PHOTOGRAPHY PREMIUM
  PORTFOLIO PAGE
  ============================================================

  Portfolio on tarkoituksella kuvavetoinen.

  Hero johdattaa sivulle, jonka jälkeen kuvat muodostavat
  tiiviin editorial-henkisen galleriaseinän.
*/

export default function PortfolioPage() {
  const {
    t,
  } = useTranslation(
    "common"
  );

  const location =
    useLocation();

  const language =
    getLanguageFromPath(
      location.pathname
    );

  return (
    <>
      {/* ======================================================
          PORTFOLIO HERO
          ====================================================== */}

      <section
        className="
          relative
          overflow-hidden
          bg-canvas
        "
      >
        {/* HERO IMAGE */}

        <motion.div
          className="
            pointer-events-none
            absolute
            inset-y-0
            right-0
            z-0
            w-[60%]
            overflow-hidden

            max-sm:right-[-12%]
            max-sm:w-[112%]
          "
          initial={{
            opacity: 0,
            scale: 1.035,
          }}
          animate={{
            opacity: 1,
            scale: 1,
          }}
          transition={{
            duration: 1.4,
            delay: 0.1,
            ease: [
              0.22,
              1,
              0.36,
              1,
            ],
          }}
          aria-hidden="true"
        >
          <img
            src="/media/heroes/portfolio-hero.webp"
            alt=""
            loading="eager"
            decoding="async"
            className="
              h-full
              w-full
              object-cover
              opacity-[0.82]
              saturate-[0.82]
              contrast-[1.04]
            "
            style={{
              objectPosition:
                "50% 50%",
            }}
          />

          <div
            className="
              absolute
              inset-0
              bg-gradient-to-r
              from-canvas
              via-canvas/25
              to-transparent
            "
          />

          <div
            className="
              absolute
              inset-0
              bg-gradient-to-b
              from-canvas/10
              via-transparent
              to-canvas/20
            "
          />
        </motion.div>

        {/* TEXT SCRIM */}

        <div
          className="
            pointer-events-none
            absolute
            inset-y-0
            left-0
            z-[1]
            w-[62%]
            bg-gradient-to-r
            from-canvas
            via-canvas/92
            to-transparent

            max-sm:w-[88%]
          "
          aria-hidden="true"
        />

        {/* CONTENT */}

        <div
          className="
            relative
            z-10
            mx-auto
            grid
            min-h-[58svh]
            w-full
            max-w-editorial
            gap-10
            px-6
            pb-16
            pt-32

            sm:px-10
            sm:pb-20
            sm:pt-36

            lg:grid-cols-[minmax(0,1fr)_minmax(260px,0.32fr)]
            lg:items-end
            lg:gap-16
            lg:px-16
            lg:pb-24
          "
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 24,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.9,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            <HandwrittenEyebrow
              label={t(
                "portfolioPage.eyebrow"
              )}
            />

            <h1
              className="
                m-0
                max-w-[960px]
                font-display
                text-[clamp(4rem,9vw,9rem)]
                font-normal
                leading-[0.84]
                tracking-[-0.055em]
                text-text
              "
            >
              {t(
                "portfolioPage.title"
              )}
            </h1>
          </motion.div>

          <motion.div
            className="
              max-w-md
            "
            initial={{
              opacity: 0,
              y: 18,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.8,
              delay: 0.16,
            }}
          >
            <div
              className="
                mb-6
                h-px
                w-10
                bg-text
              "
            />

            <p
              className="
                m-0
                text-sm
                leading-7
                text-text/80

                sm:text-base
                sm:leading-8
              "
            >
              {t(
                "portfolioPage.description"
              )}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ======================================================
          PORTFOLIO WALL
          ====================================================== */}

      <AlbumShowcase
        language={
          language
        }
      />

      {/* ======================================================
          FINAL CTA
          ====================================================== */}

      <section
        className="
          border-t
          border-border
          bg-canvas
        "
      >
        <div
          className="
            mx-auto
            grid
            w-full
            max-w-editorial
            gap-12
            px-6
            py-24

            sm:px-10
            sm:py-32

            lg:grid-cols-[1fr_auto]
            lg:items-end
            lg:px-16
            lg:py-36
          "
        >
          <div>
            <p
              className="
                mb-5
                text-[9px]
                uppercase
                tracking-[0.28em]
                text-muted
              "
            >
              02 /{" "}
              {t(
                "portfolioPage.ctaEyebrow"
              )}
            </p>

            <h2
              className="
                m-0
                max-w-4xl
                font-display
                text-[clamp(3rem,6vw,6rem)]
                font-normal
                leading-[0.9]
                tracking-[-0.045em]
                text-text
              "
            >
              {t(
                "portfolioPage.ctaTitle"
              )}
            </h2>
          </div>

          <Link
            to={getRoutePath(
              "services",
              language
            )}
            className="
              group
              inline-flex
              min-h-12
              items-center
              gap-4
              border-b
              border-text
              pb-2
              text-[10px]
              font-semibold
              uppercase
              tracking-[0.2em]
              text-text
              no-underline
            "
          >
            {t(
              "portfolioPage.viewSessions"
            )}

            <span
              className="
                transition-transform
                duration-300
                group-hover:translate-x-1
              "
            >
              →
            </span>
          </Link>
        </div>
      </section>

      <Footer
        language={
          language
        }
      />
    </>
  );
}